import type { BackendConfig } from './gateway/types.ts'
import { normalizeAthenaGatewayBaseUrl } from './gateway/url.ts'
import { buildSdkHeaderValue } from './sdk-version.ts'
import {
  buildAthenaRequestHeaders,
  hasHeaderIgnoreCase,
} from './utils/athena-request-headers.ts'

const SDK_NAME = 'xylex-group/athena'

export type AthenaRequestService = 'db' | 'auth' | 'chat' | 'storage'
export type AthenaRequestMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'DELETE'
  | 'HEAD'
  | 'OPTIONS'

export interface AthenaRequestQueryValueMap {
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | Array<string | number | boolean | null | undefined>
}

export interface AthenaRequestOptions {
  service?: AthenaRequestService
  url?: string
  path?: string
  method?: AthenaRequestMethod
  headers?: Record<string, string>
  /** Per-request override for the canonical `X-Athena-Key` header. */
  apiKey?: string | null
  query?: AthenaRequestQueryValueMap
  body?: RequestInit['body'] | Record<string, unknown> | unknown[] | null
  signal?: AbortSignal
  credentials?: RequestInit['credentials']
  responseType?: 'json' | 'text' | 'response'
}

export interface AthenaRequestResponse<T = unknown> {
  ok: boolean
  status: number
  statusText: string
  headers: Headers
  data: T | string | null
  raw: Response
}

export interface AthenaRawRequestConfig {
  baseUrl?: string
  authUrl?: string
  chatUrl?: string
  storageUrl?: string
  apiKey?: string
  client?: string | null
  backend?: BackendConfig
  pgUri?: string | null
  jdbcUrl?: string | null
  headers?: Record<string, string>
}

export interface AthenaRawRequestContext {
  userId?: string | null
  organizationId?: string | null
  headers?: Record<string, string>
  cookie?: string | null
  bearerToken?: string | null
  sessionToken?: string | null
  forceNoCache?: boolean
}

function parseArbitraryResponseBody(rawText: string, contentType: string | null): unknown {
  if (!rawText) {
    return null
  }
  if (contentType?.toLowerCase().includes('application/json')) {
    try {
      return JSON.parse(rawText)
    } catch {
      return rawText
    }
  }
  const trimmed = rawText.trim()
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      return JSON.parse(trimmed)
    } catch {
      return rawText
    }
  }
  return rawText
}

function toRequestQueryString(query?: AthenaRequestQueryValueMap): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, rawValue] of Object.entries(query)) {
    const values = Array.isArray(rawValue) ? rawValue : [rawValue]
    for (const value of values) {
      if (value === undefined) continue
      params.append(key, value === null ? '' : String(value))
    }
  }
  const serialized = params.toString()
  return serialized ? `?${serialized}` : ''
}

function isJsonBody(body: AthenaRequestOptions['body']): boolean {
  return (
    body !== undefined &&
    body !== null &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof URLSearchParams) &&
    !(body instanceof ArrayBuffer) &&
    !ArrayBuffer.isView(body) &&
    typeof body !== 'string'
  )
}

export function createAthenaRequest(
  config: AthenaRawRequestConfig,
  resolveContext: () =>
    | AthenaRawRequestContext
    | undefined
    | Promise<AthenaRawRequestContext | undefined>,
): <T = unknown>(options: AthenaRequestOptions) => Promise<AthenaRequestResponse<T>> {
  return async <T = unknown>(options: AthenaRequestOptions): Promise<AthenaRequestResponse<T>> => {
    const context = await resolveContext()
    const method = options.method ?? 'GET'
    const responseType = options.responseType ?? 'json'
    const service = options.service ?? 'db'
    const absoluteUrl = options.url?.trim()
    const baseUrlByService: Record<AthenaRequestService, string | undefined> = {
      db: config.baseUrl,
      auth: config.authUrl,
      chat: config.chatUrl,
      storage: config.storageUrl,
    }

    const queryString = toRequestQueryString(options.query)
    let targetUrl: string
    if (absoluteUrl) {
      // Absolute URLs bypass gateway base-url normalization so query/hash are preserved
      // and auth-only / storage-only clients can still call arbitrary endpoints.
      try {
        const parsed = new URL(absoluteUrl)
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          throw new Error('not http(s)')
        }
      } catch {
        throw new Error(
          `client.request(...) url must be an absolute http(s) URL. Received ${JSON.stringify(options.url)}.`,
        )
      }
      targetUrl = `${absoluteUrl}${queryString}`
    } else {
      const resolvedBaseUrl = baseUrlByService[service]
      if (!resolvedBaseUrl) {
        throw new Error(
          `Athena ${service} base URL is not configured. Pass createClient({ url }) for unified routing or set the service-specific URL first.`,
        )
      }

      const normalizedBaseUrl = normalizeAthenaGatewayBaseUrl(resolvedBaseUrl, {
        label: `Athena ${service} base URL`,
      })
      const path = options.path?.trim()
      if (!path) {
        throw new Error('client.request(...) requires either an absolute url or a non-empty path.')
      }
      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      targetUrl = `${normalizedBaseUrl}${normalizedPath}${queryString}`
    }

    const requestProfile =
      service === 'auth'
        ? 'auth'
        : service === 'chat'
          ? 'chat'
          : service === 'storage'
            ? 'storage'
            : 'gateway'
    const headers = buildAthenaRequestHeaders({
      profile: requestProfile,
      sdkHeaderValue: buildSdkHeaderValue(SDK_NAME),
      apiKey: options.apiKey ?? config.apiKey,
      client: config.client,
      userId: context?.userId,
      organizationId: context?.organizationId,
      backend: config.backend,
      pgUri: config.pgUri,
      jdbcUrl: config.jdbcUrl,
      bearerToken: context?.bearerToken,
      cookie: context?.cookie,
      sessionToken: context?.sessionToken,
      forceNoCache: context?.forceNoCache,
      configHeaders: {
        ...(config.headers ?? {}),
        ...(context?.headers ?? {}),
      },
      callHeaders: options.headers,
      accept: service === 'chat' ? 'application/json' : undefined,
      contentType:
        service === 'auth' || service === 'db' || service === 'storage'
          ? 'application/json'
          : undefined,
      stripNulls: service === 'db' ? true : undefined,
    })

    const shouldSendJsonBody = isJsonBody(options.body)
    if (shouldSendJsonBody && !hasHeaderIgnoreCase(headers, 'Content-Type')) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(targetUrl, {
      method,
      headers,
      body:
        options.body === undefined || options.body === null
          ? undefined
          : shouldSendJsonBody
            ? JSON.stringify(options.body)
            : (options.body as RequestInit['body']),
      signal: options.signal,
      credentials: options.credentials,
    })

    if (responseType === 'response') {
      return {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: null,
        raw: response,
      }
    }

    const rawText = await response.text()
    const parsed =
      responseType === 'text'
        ? rawText
        : parseArbitraryResponseBody(rawText, response.headers.get('content-type'))

    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data: parsed as T | string | null,
      raw: response,
    }
  }
}

